import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Fab,
  LinearProgress,
  TextField,
  Typography
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FilterIcon from "@material-ui/icons/FilterList";

import AddIcon from "@material-ui/icons/Add";
import InvoiceIcon from "@material-ui/icons/Receipt";
import axios from "axios";
import api from "../common/api";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const Invoices = ({ client_id }) => {
  const [data, setData] = useState([]);
  const [snackBar, setSnackBar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [sendingInvoices, setSendingInvoices] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filters, setFilters] = useState([
    [],
    [],
    [],
    [],
    [],
    [],
    !client_id ? ["sap error", "pending"] : [],
    [],
    [],
    [],
    [],
    []
  ]);
  const [orderColumn, setOrderColumn] = useState("");
  const [orderOrder, setOrderOrder] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceDateRange, setInvoiceDateRange] = useState({
    from: "",
    to: ""
  });
  const [invoicesToSend, setInvoicesToSend] = useState([]);

  const getMuiTheme = () =>
    createMuiTheme({
      overrides: {
        MuiTableCell: {
          root: {
            padding: "0.25rem",
            fontSize: "0.8rem",
            maxWidth: "10rem"
          }
        }
      }
    });

  const fetchData = async (page, filterList, column, order, q_rows) => {
    setIsLoading(true);
    let url = api.invoices().index(page + 1, "", "", column, order, q_rows);
    if (filterList[1].length !== 0) {
      url += `&number=${filterList[1].join(",")}`;
    }
    if (filterList[2].length !== 0) {
      url += `&sap_code=${filterList[2].join(",")}`;
    }
    if (filterList[3].length !== 0) {
      const [from, to] = filterList[3];
      if (from) url += `&invoice_date_from=${from}`;
      if (to) url += `&invoice_date_to=${to}`;
    }
    if (filterList[4].length !== 0) {
      url += `&client.business_name=${filterList[4].join(",")}`;
    }
    if (filterList[6].length !== 0) {
      url += `&status=${filterList[6].join(",")}`;
    }
    if (filterList[11].length !== 0) {
      url += `&client_id=${filterList[11].join(",")}`;
    }
    if (client_id) {
      url += `&client_id=${client_id}`;
    }
    if (filterList[7].length !== 0) {
      url += `&point_of_sales=${filterList[7].join(",")}`;
    }
    if (filterList[8].length !== 0) {
      url += `&level_1=${filterList[8].join(",")}`;
    }
    if (filterList[9].length !== 0) {
      url += `&level_2=${filterList[9].join(",")}`;
    }

    const token = localStorage.getItem("userToken");
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data.data);
      setCount(res.data.meta.total);
      setRowsPerPage(res.data.meta.per_page);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, filters, orderColumn, orderOrder, rowsPerPage);
  }, [page, filters, orderColumn, orderOrder, rowsPerPage]);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "id" ? orderOrder : null
      }
    },
    {
      name: "number",
      label: "Número",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "invoice_number" ? orderOrder : null
      }
    },
    {
      name: "sap_code",
      label: "Código SAP",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "invoice_date",
      label: "Fecha factura",
      options: {
        filter: true,
        filterType: "custom",
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <div>
              <TextField
                label="Fecha desde"
                InputLabelProps={{
                  shrink: true
                }}
                type="date"
                value={filterList[index][0] || ""}
                onChange={event => {
                  filterList[index][0] = event.target.value;
                  onChange(filterList[index], index, column);
                }}
                style={{ width: "45%" }}
              />
              <TextField
                label="Fecha hasta"
                type="date"
                InputLabelProps={{
                  shrink: true
                }}
                value={filterList[index][1] || ""}
                onChange={event => {
                  filterList[index][1] = event.target.value;
                  onChange(filterList[index], index, column);
                }}
                style={{ width: "45%" }}
              />
            </div>
          )
        },
        customFilterListRender: v => {
          if (!v || v.length === 0) return null;
          const [from, to] = v;
          return [`Desde: ${from || "..."}`, `Hasta: ${to || "..."}`];
        },
        onFilterChipClose: () => {
          setInvoiceDateRange({ from: "", to: "" });
          const newFilterList = [...filters];
          newFilterList[3] = [];
          setFilters(newFilterList);
        },
        sort: true,
        sortDirection: orderColumn === "date" ? orderOrder : null
      }
    },
    {
      name: "client.business_name",
      label: "Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "total",
      label: "Total",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "total" ? orderOrder : null,
        customBodyRender: value => {
          if (typeof value === "string" && value.includes("$")) {
            return value;
          }
          return new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS"
          }).format(value);
        }
      }
    },
    {
      name: "status",
      label: "Estado",
      options: {
        filter: true,
        filterType: "select",
        filterOptions: {
          names: ["pending", "sap error", "generated", "arca error"]
        },
        sort: false
      }
    },
    {
      name: "point_of_sales.name",
      label: "Punto de venta",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "level_1",
      label: "Agrupación",
      options: {
        filter: true,
        display: false,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "level_2",
      label: "Agrupacion 2",
      options: {
        filter: true,
        display: false,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "due_date",
      label: "Fecha de vencimiento",
      options: {
        filter: true,
        display: false,
        filterType: "custom",
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <TextField
              label="Fecha de vencimiento"
              type="date"
              InputLabelProps={{
                shrink: true
              }}
              value={filterList[index] || ""}
              onChange={event => {
                filterList[index] = event.target.value;
                onChange(filterList[index], index, column);
              }}
            />
          )
        },
        customFilterListRender: v => (v ? `Vencimiento: ${v}` : null),
        sort: true,
        sortDirection: orderColumn === "due_date" ? orderOrder : null
      }
    },
    {
      name: "client_id",
      label: "Id Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "client.sap_code",
      label: "Cod SAP Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "cae",
      label: "CAE",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "is_fce",
      label: "FCE",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false,
        customBodyRender: value => {
          return value ? "Sí" : "No";
        }
      }
    },
    {
      name: "can_edit",
      label: "",
      options: {
        filter: false,
        filterType: "textField",
        sort: false,
        display: false,
        viewColumns: false
      }
    },
    {
      name: "sap_error",
      label: "Error SAP",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "creator.name",
      label: "Creador",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "Acciones",
      label: "",
      options: {
        filter: false,
        setCellHeaderProps: () => ({ align: "center" }),
        customBodyRender: (value, tableMeta, updateValue) => {
          const rowId = tableMeta.rowData[0];
          //const rowId = 1
          return (
            <div className="row" style={{ display: "block" }}>
              <Fab
                color="primary"
                aria-label="view"
                variant="extended"
                style={{ boxShadow: "none", backgroundColor: "green" }}
                fontSize="small"
                className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
                href={"/invoice/" + rowId}
              >
                <EditIcon fontSize="small" />
              </Fab>
            </div>
          );
        }
      }
    }
  ];

  const handleFilterData = (selectedRows, displayData) => {
    console.log("Selected Rows:", selectedRows);
    /*
    {
    "lookup": {
        "0": true,
        "1": true,
        "4": true,
        "5": true
    },
    "data": [
        {
            "index": 1,
            "dataIndex": 1
        },
        {
            "index": 0,
            "dataIndex": 0
        },
        {
            "index": 5,
            "dataIndex": 5
        },
        {
            "index": 4,
            "dataIndex": 4
        }
    ]
}
    
    */
    let selectedData = [];
    selectedRows.data.forEach(element => {
      selectedData.push(displayData[element.dataIndex].data);
    });
    setData(selectedData);
    setInvoicesToSend(selectedData);
  };

  const handleSendInvoices = async () => {
    setSendingInvoices(true);
    const token = localStorage.getItem("userToken");
    const invoiceIds = invoicesToSend.map(invoice => invoice[0]);
    await axios
      .post(
        api.invoices().retryAll(),
        { ids: invoiceIds },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      .then(res => {
        const data = res.data;
        if (data.filter(i => i.success == false).length > 0) {
          setSnackBar({
            open: true,
            message:
              "Algunas facturas no se enviaron correctamente. Revisar columna error",
            severity: "warning"
          });
          setSendingInvoices(false);
          return;
        } else {
          setSnackBar({
            open: true,
            message: "Facturas enviadas correctamente",
            severity: "success"
          });
          setSendingInvoices(false);
        }
        setInvoicesToSend([]);
        fetchData(page, filters, orderColumn, orderOrder, rowsPerPage);
      })
      .catch(err => {
        setSnackBar({
          open: true,
          message: "Error al enviar las facturas",
          severity: "error"
        });
        setSendingInvoices(false);
        console.error(err);
      });
  };

  let options = {
    serverSide: true,
    count: count,
    page: page,
    rowsPerPageOptions: [10, 25, 50, 100, rowsPerPage],
    onChangePage: setPage,
    onChangeRowsPerPage: number => setRowsPerPage(number),
    onColumnSortChange: (column, direction) => {
      setOrderColumn(column);
      setOrderOrder(direction);
    },
    customToolbar: () => {
      return (
        <Tooltip title={"Nuevo"}>
          <IconButton
            onClick={e => {
              window.location.href = "invoice/0";
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      );
    },
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => {
      return (
        <>
          {invoicesToSend.length == 0 && (
            <Tooltip title={"Filtrar selección"}>
              <IconButton
                onClick={e => {
                  handleFilterData(selectedRows, displayData);
                  setSelectedRows([]);
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
          )}
          {invoicesToSend.length > 0 && (
            <Tooltip title={"Enviar facturas seleccionadas"}>
              <IconButton
                disabled={sendingInvoices}
                onClick={e => {
                  handleSendInvoices();
                }}
                onDoubleClick={e => {
                  console.log("invoicesToSend", invoicesToSend);
                }}
              >
                <InvoiceIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    },
    customFilterDialogFooter: filterList => {
      return (
        <div style={{ marginTop: "40px" }}>
          <Button
            variant="contained"
            onClick={() => {
              setPage(0);
              setFilters(filterList);
            }}
          >
            Aplicar
          </Button>
        </div>
      );
    },

    textLabels: {
      body: {
        noMatch: isLoading ? (
          <CircularProgress size={24} />
        ) : (
          "No hay datos disponibles"
        )
      }
    }
  };
  if (isLoading) {
    return (
      <div>
        <Typography variant="h6">Cargando...</Typography>
        <LinearProgress />
      </div>
    );
  }
  if (!isLoading) options.rowsPerPage = rowsPerPage;
  return (
    <>
      <Snackbar
        open={snackBar.open}
        onClose={value => setSnackBar({ ...snackBar, open: false })}
      >
        <Alert
          onClose={value => setSnackBar({ ...snackBar, open: false })}
          severity={snackBar.severity}
        >
          {snackBar.message}
        </Alert>
      </Snackbar>
      <MuiThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title="Facturas"
          data={data}
          columns={columns}
          options={options}
        />
      </MuiThemeProvider>
    </>
  );
};

export default Invoices;
